import ShopSideBar from '../../components/SideBar/ShopSideBar';
import { useCartContext } from '../../../context/CartContext/CartContext';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useMemo, useEffect } from 'react';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { NavLink, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { UserCustomerType } from '../../../models/User/contracts';
import { InputField } from '../../../Admin/components/InputField/InputField';
import { ErrorMessageLabel } from '../../../Admin/components/ErrorMessageLabel/ErrorMessageLabel';
import { toast } from 'react-toastify';
import { Order } from '../../../models/Order/Order';
import { OrderCreatePayload } from '../../../models/Order/contracts';
import { Scrollbars } from 'react-custom-scrollbars';
import cx from 'classnames';
import style from './checkout.module.scss';
import { Form as BootstrapForm } from 'react-bootstrap';
import DayjsDatePicker from '../../../Admin/components/DayjsDatePicker/DayjsDatePicker';
import { disabledDateWeekend } from '../../../utils/date-utils';
import dayjs from 'dayjs';

const CheckoutValidationScheme = Yup.object({
  full_name: Yup.string().required(
    'Potrebno je da unesete ime osobe koja šalje zahtev.'
  ),
  type: Yup.string().required(),
  personal_id_number: Yup.string().when('type', {
    is: UserCustomerType.INDIVIDUAL,
    then: () => Yup.string().required('Broj ličnog dokumenta je obavezan.'),
    otherwise: () => Yup.string().notRequired()
  }),
  company_name: Yup.string().when('type', {
    is: UserCustomerType.COMPANY,
    then: () => Yup.string().required('Naziv kompanije je obavezan.')
  }),
  company_tax_number: Yup.mixed().when('type', {
    is: UserCustomerType.COMPANY,
    then: () =>
      Yup.string()
        .min(9, 'Broj mora sadržati najmanje 9 cifara')
        .required('PIB je obavezno polje')
  }),
  company_id_number: Yup.mixed().when('type', {
    is: UserCustomerType.COMPANY,
    then: () =>
      Yup.string()
        .min(8, 'Broj mora sadržati najmanje 8 cifara')
        .required('Matični broj je obavezno polje')
  }),
  email: Yup.string()
    .email('Email nije validan')
    .required('Potrebno je da unesete email'),
  state: Yup.string().required('Država je obavezno polje'),
  city: Yup.string().required('Grad je obavezno polje'),
  zip: Yup.string().required('Poštanski broj je obavezno polje'),
  address: Yup.string().required('Adresa je obavezno polje'),
  reservation_start: Yup.date().required('Unesite datum početka rezervacije'),
  reservation_end: Yup.date()
    .required('Unesite datum kraja rezervacije')
    .min(
      Yup.ref('reservation_start'),
      'Datum kraja rezervacije mora biti veći od datuma početka rezervacije'
    )
});

// A new helper component to manage synchronization without causing resets
const FormStateSynchronizer = () => {
  const { cart, includeWashingFee } = useCartContext();
  const { setFieldValue } = useFormikContext<OrderCreatePayload>();

  useEffect(() => {
    if (cart?.startDate) {
      setFieldValue('reservation_start', cart.startDate);
    }
  }, [cart?.startDate, setFieldValue]);

  useEffect(() => {
    if (cart?.endDate) {
      setFieldValue('reservation_end', cart.endDate);
    }
  }, [cart?.endDate, setFieldValue]);

  useEffect(() => {
    setFieldValue('washing_fee', includeWashingFee);
  }, [includeWashingFee, setFieldValue]);

  return null; // This component renders nothing
};

function Checkout({ collapsedSidebar, setCollapsedSidebar }: any) {
  const {
    cart,
    cartClear,
    cartSetStartEndDates,
    includeWashingFee,
    setIncludeWashingFee
  } = useCartContext();
  const navigate = useNavigate();

  const calculatePrices = () => {
    if (!cart) {
      return { baseTotal: 0, fee: 0, pdv: 0, finalTotal: 0 };
    }
    const baseTotal = cart.totalWithFactor;
    const dynamicWashingFee = baseTotal * 0.1;
    const fee = includeWashingFee ? dynamicWashingFee : 0;
    const totalBeforePdv = baseTotal + fee;
    const pdv = totalBeforePdv * 0.2;
    const finalTotal = totalBeforePdv + pdv;
    return { baseTotal, fee, pdv, finalTotal };
  };

  const prices = calculatePrices();

  // initialValues are now only calculated once when the component mounts
  const initialValues: OrderCreatePayload = {
    cart_token: cart?.token ?? '',
    full_name: '',
    type: UserCustomerType.INDIVIDUAL,
    personal_id_number: '',
    company_name: '',
    company_tax_number: '',
    company_id_number: '',
    email: '',
    phone: '',
    state: 'Srbija',
    city: '',
    address: '',
    zip: '',
    note: '',
    washing_fee: includeWashingFee,
    reservation_start: cart?.startDate ?? '',
    reservation_end: cart?.endDate ?? ''
  };

  return (
    <div className={style.pageContainer}>
      <ShopSideBar
        collapsed={collapsedSidebar}
        setCollapsed={setCollapsedSidebar}
        heading="Vaša porudžbina"
      >
        <div className={style.checkoutOrderWrapper}>
          <div className={style.products}>
            <Scrollbars className={style.scrollbarWrapper}>
              {cart?.products.map((product) => (
                <div className={style.product} key={product.id}>
                  <div className={style.imageWrapper}>
                    <img
                      src={product.getMainImageUrl() ?? ''}
                      alt={product.name}
                    />
                  </div>
                  <div className={style.info}>
                    <div className={style.name}>{product.name}</div>
                  </div>
                </div>
              ))}
            </Scrollbars>
          </div>
          <div className={style.footer}>
            <div className={style.priceSummary}>
              <div className={style.priceRow}>
                <span>Cena (sa faktorom):</span>
                <span>
                  {prices.baseTotal.toFixed(2)}&nbsp;
                  {process.env.REACT_APP_DEFAULT_CURRENCY || 'RSD'}
                </span>
              </div>
              <div className={style.priceRow}>
                <span>Naknada za pranje (10%):</span>
                <span>
                  {prices.fee.toFixed(2)}&nbsp;
                  {process.env.REACT_APP_DEFAULT_CURRENCY || 'RSD'}
                </span>
              </div>
              <div className={style.priceRow}>
                <span>PDV (20%):</span>
                <span>
                  {prices.pdv.toFixed(2)}&nbsp;
                  {process.env.REACT_APP_DEFAULT_CURRENCY || 'RSD'}
                </span>
              </div>
              <div className={`${style.priceRow} ${style.summaryTotal}`}>
                <span>Ukupna cena:</span>
                <span>
                  {prices.finalTotal.toFixed(2)}&nbsp;
                  {process.env.REACT_APP_DEFAULT_CURRENCY || 'RSD'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </ShopSideBar>

      <div
        className={cx(
          style.checkoutPageWrapper,
          collapsedSidebar ? style.collapsed : null,
          'cs-content'
        )}
      >
        <h1 className={style.title}>Poručivanje</h1>
        <Container className={style.checkoutInfoWrapper}>
          <Formik
            initialValues={initialValues}
            validationSchema={CheckoutValidationScheme}
            // REMOVED enableReinitialize to prevent form resets
            onSubmit={(values, { setSubmitting }) => {
              const finalValues = { ...values, washing_fee: includeWashingFee };

              setSubmitting(true);
              Order.create(finalValues)
                .then(() => {
                  toast.success('Vaš zahtev je uspešno poslat!');
                  cartClear();
                  navigate('/thank-you');
                })
                .catch((error) => {
                  console.error('Order creation failed:', error);
                  toast.error('Došlo je do greške prilikom slanja zahteva.');
                })
                .finally(() => {
                  setSubmitting(false);
                });
            }}
          >
            {({ values, isSubmitting, isValid, setFieldValue }) => (
              <Form>
                {/* This helper component will now handle updates */}
                <FormStateSynchronizer />

                <Row>
                  <Col>
                    <div className={style.formGroup}>
                      <label htmlFor="reservation_start">
                        Početak rezervacije:
                      </label>
                      <DayjsDatePicker
                        name="reservation_start"
                        inputReadOnly
                        className="w-100"
                        format="YYYY-MM-DD"
                        placeholder="Izaberite datum"
                        value={
                          values.reservation_start
                            ? dayjs(values.reservation_start)
                            : null
                        }
                        onChange={(date: any, dateString: any) => {
                          setFieldValue('reservation_start', dateString);
                          cartSetStartEndDates(
                            dateString,
                            values.reservation_end.toString()
                          );
                        }}
                        disabledDate={disabledDateWeekend}
                      />
                      <ErrorMessageLabel name="reservation_start" />
                    </div>
                  </Col>
                  <Col>
                    <div className={style.formGroup}>
                      <label htmlFor="reservation_end">Kraj rezervacije:</label>
                      <DayjsDatePicker
                        name="reservation_end"
                        inputReadOnly
                        className="w-100" // ensure it takes full width
                        format="YYYY-MM-DD"
                        placeholder="Izaberite datum"
                        value={
                          values.reservation_end
                            ? dayjs(values.reservation_end)
                            : null
                        }
                        onChange={(date: any, dateString: any) => {
                          setFieldValue('reservation_end', dateString);
                          cartSetStartEndDates(
                            values.reservation_start.toString(),
                            dateString
                          );
                        }}
                        disabledDate={disabledDateWeekend}
                      />
                      <ErrorMessageLabel name="reservation_end" />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <div className={style.formGroup}>
                    <label htmlFor="type">Odaberite način poručivanja:</label>
                    <div className={style.userRoleSwitch}>
                      <div
                        className={cx(
                          style.button,
                          values.type === UserCustomerType.INDIVIDUAL
                            ? style.active
                            : null
                        )}
                        onClick={() => {
                          setFieldValue('type', UserCustomerType.INDIVIDUAL);
                        }}
                      >
                        Fizičko lice
                      </div>
                      <div
                        className={cx(
                          style.button,
                          values.type === UserCustomerType.COMPANY
                            ? style.active
                            : null
                        )}
                        onClick={() => {
                          setFieldValue('type', UserCustomerType.COMPANY);
                        }}
                      >
                        Pravno lice
                      </div>
                    </div>
                  </div>
                </Row>

                <Row>
                  <Col>
                    <div className={style.formGroup}>
                      <label htmlFor="full_name">Ime:</label>
                      <Field name="full_name" type="text" as={InputField} />
                      <ErrorMessageLabel name="full_name" />
                    </div>
                  </Col>
                  {values.type === UserCustomerType.INDIVIDUAL && (
                    <div className={style.formGroup}>
                      <label htmlFor="personal_id_number">
                        Broj lične karte/pasoša:
                      </label>
                      <Field
                        name="personal_id_number"
                        type="text"
                        as={InputField}
                      />
                      <ErrorMessageLabel name="personal_id_number" />
                    </div>
                  )}

                  {values.type === UserCustomerType.COMPANY && (
                    <Col>
                      <div className={style.formGroup}>
                        <label htmlFor="company_name">Naziv kompanije:</label>
                        <Field
                          name="company_name"
                          type="text"
                          as={InputField}
                        />
                        <ErrorMessageLabel name="company_name" />
                      </div>
                    </Col>
                  )}
                </Row>

                {values.type === UserCustomerType.COMPANY && (
                  <Row>
                    <Col>
                      <div className={style.formGroup}>
                        <label htmlFor="company_id_number">Matični broj:</label>
                        <Field
                          name="company_id_number"
                          type="text"
                          as={InputField}
                        />
                        <ErrorMessageLabel name="company_id_number" />
                      </div>
                    </Col>
                    <Col>
                      <div className={style.formGroup}>
                        <label htmlFor="company_tax_number">PIB:</label>
                        <Field
                          name="company_tax_number"
                          type="text"
                          as={InputField}
                        />
                        <ErrorMessageLabel name="company_tax_number" />
                      </div>
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col>
                    <div className={style.formGroup}>
                      <label htmlFor="email">Email adresa:</label>
                      <Field name="email" type="email" as={InputField} />
                      <ErrorMessageLabel name="email" />
                    </div>
                  </Col>
                  <Col>
                    <div className={style.formGroup}>
                      <label htmlFor="phone">Broj telefona:</label>
                      <Field name="phone" type="text" as={InputField} />
                      <ErrorMessageLabel name="phone" />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className={style.formGroup}>
                      <label htmlFor="state">Država:</label>
                      <Field name="state" type="text" as={InputField} />
                      <ErrorMessageLabel name="state" />
                    </div>
                  </Col>
                  <Col>
                    <div className={style.formGroup}>
                      <label htmlFor="city">Grad:</label>
                      <Field name="city" type="text" as={InputField} />
                      <ErrorMessageLabel name="city" />
                    </div>
                  </Col>
                  <Col>
                    <div className={style.formGroup}>
                      <label htmlFor="zip">Poštanski broj:</label>
                      <Field name="zip" type="text" as={InputField} />
                      <ErrorMessageLabel name="zip" />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className={style.formGroup}>
                      <label htmlFor="address">Adresa:</label>
                      <Field name="address" type="text" as={InputField} />
                      <ErrorMessageLabel name="address" />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className={style.formGroup}>
                      <label htmlFor="note">Dodatne napomene:</label>
                      <Field
                        name="note"
                        as="textarea"
                        className={style.textareaField}
                      />
                      <ErrorMessageLabel name="note" />
                    </div>
                  </Col>
                </Row>

                {/* --- UPDATED WASHING FEE CHECKBOX --- */}
                <Row>
                  <Col>
                    <div
                      className={`${style.formGroup} ${style.washingFeeToggle}`}
                    >
                      <BootstrapForm.Check
                        type="switch"
                        id="washing-fee-checkout-switch"
                        name="washing_fee"
                        label="Dodaj naknadu za pranje kostima (10%)"
                        checked={includeWashingFee}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const isChecked = e.target.checked;
                          // Update the global context state directly
                          setIncludeWashingFee(isChecked);
                        }}
                      />
                      <ErrorMessageLabel name="washing_fee" />
                    </div>
                  </Col>
                </Row>

                <div className={style.footer}>
                  <button
                    className="cs-secondary-button"
                    onClick={() => navigate('/cart')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <i className="fa fa-arrow-left" aria-hidden="true"></i>
                    Nazad
                  </button>
                  <button
                    type="submit"
                    className="cs-primary-button-full"
                    disabled={!isValid || isSubmitting}
                  >
                    Pošalji zahtev
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </Container>
      </div>
    </div>
  );
}

export default Checkout;
